import { Button, ButtonColor, ButtonSize, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Card, Customer } from 'src/front/api/apiInstance';
import { CardListData, CardSelectionType, TransferableCardData } from 'src/front/api/generated';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import CustomerSearchForm, {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components//CustomerSearchForm';
import FormModal from 'src/front/components//FormModal';

import {
  ButtonRow,
  Subtitle,
  TableContainer,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import {
  useCustomerSearchDispatch,
  useTransferableCardCustomerDataDispatch,
} from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

const LongerTableContainer = styled(TableContainer)`
  width: 1000px;
  max-width: none;
`;

const LongerButtonRow = styled(ButtonRow)`
  width: 1000px;
`;

const customerTableHeaders = [
  'cardEntry_customerTable_name_title',
  'cardEntry_customerInfo_middleName_label',
  'cardEntry_customerTable_birth_title',
  'cardEntry_customerInfo_gender_label',
  'cardEntry_customerTable_document_title',
];

const cardTableHeaders = [
  'general_cardNumber_label',
  'general_status_label',
  'general_currency_label',
  'general_expiredDate_label',
];
const transferableCardTableHeaders = [
  'cardEntry_productInfo_productTransfer_cardProduct_text',
  'general_cardNumber_label',
  'cardEntry_productInfo_productTransfer_accountNumber_text',
];
interface Props {
  onClose: () => void;
  cardSelectionType: CardSelectionType;
  onCardSelect: (card: CardListData | TransferableCardData, formValues: SearchFormData) => void;
  isProductTransfer?: boolean;
  formValues?: SearchFormData;
}
export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}
const MainCardSelect: FC<Props> = ({
  onClose,
  cardSelectionType,
  onCardSelect,
  formValues,
  isProductTransfer,
}) => {
  const { t } = useTranslation();

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    formValues || customerSearchDefaultValues
  );
  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow>();
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [cardList, setCardList] = useState<CardListData[]>([]);
  const [transferableCardList, setTransferableCardList] = useState<TransferableCardData[]>([]);
  const [selectedCard, setSelectedCard] = useState<TableRow>();
  const [cardsLoading, setCardsLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<string>();
  const tranferableCardDispatch = useTransferableCardCustomerDataDispatch();
  const customerSearchDispatch = useCustomerSearchDispatch();

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setCustomersLoading(true);
    const {
      customerSurname,
      customerName,
      customerSecondName,
      birthDate,
      identityDocumentSerialCode,
      identityDocumentSerialNumber,
    } = formData;
    const { data } = await Customer.getCustomers({
      customerSurname,
      customerName,
      customerSecondName,
      birthDate: formatDate(birthDate),
      identityDocumentSerialCode,
      identityDocumentSerialNumber,
    });
    const customers: TableRow[] = data.customerList.map((customer) => ({
      id: customer.customerId || '',
      columns: [
        `${customer.customerName} ${customer.customerSurname}`,
        customer.customerSecondName || '',
        `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
        customer.gender || '',
        `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
      ],
      customerNumber: customer.customerNumber,
    }));
    setCustomerList(customers);
    setSearchFormValues(formData);
    setCustomersLoading(false);
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCard && setSelectedCard(undefined);
  };

  const handleSelectCustomer = async (customer: CustomerTableRow | undefined): Promise<void> => {
    setSelectedCustomer(customer);
    if (customer) {
      setCardsLoading(true);
      if (isProductTransfer) {
        try {
          const { data } = await Card.getTransferableCardData({
            customerNumber: customer.customerNumber || '',
          });
          setTransferableCardList(data.transferableCardList);
        } catch (data) {
          // Handle with ErrorBoundary
        }
      } else {
        const { data } = await Card.getCardList({
          customerNumber: customer.customerNumber,
          cardSelectionType,
        });
        setCardList(data.cardList);
      }
      setCardsLoading(false);
      selectedCard && setSelectedCard(undefined);
    }
  };

  const handleSelectCard = (card: TableRow | undefined): void => {
    setSelectedCard(card);
    warning && setWarning(undefined);
  };

  const handleContinue = (): void => {
    if (!selectedCard) {
      setWarning(t('cardEntry_productInfo_supplementary_chooseCard_label'));
    } else {
      const card = isProductTransfer
        ? transferableCardList.find((crd) => selectedCard.id === crd.transferableCardId)
        : ({
            ...cardList.find((crd) => selectedCard.id === crd.cardId),
            customerNumber: selectedCustomer?.customerNumber || '',
          } as CardListData);
      card && onCardSelect(card, searchFormValues);
      if (isProductTransfer) {
        tranferableCardDispatch(selectedCustomer);
        const [docSeries, docNumber] = selectedCustomer?.columns[4].trim().split('-') || [];
        customerSearchDispatch({
          isNewCustomer: false,
          customerSearchValues: {
            ...searchFormValues,
            customerSecondName: selectedCustomer?.columns[1] || '',
            identityDocumentSerialCode: docSeries,
            identityDocumentSerialNumber: docNumber,
          },
        });
      }
    }
  };

  const cardTableData: TableRow[] = cardList.map((card) => ({
    id: card.cardId,
    columns: [card.cardNumber, card.status, card.currency, card.expiredDate],
  }));
  const transferableCardTableData: TableRow[] = transferableCardList.map((card) => ({
    id: card.transferableCardId,
    columns: [
      card.transferableProductName,
      card.transferableCardNumber,
      card.transferableCardAccountNumber,
    ],
  }));

  return (
    <FormModal onClose={onClose} zIndex={200}>
      <Title>
        <Trans
          i18nKey={
            isProductTransfer
              ? 'cardEntry_productInfo_productTransfer_selectSourceCard_title'
              : 'cardEntry_supplementaryCardDetail_title'
          }
        />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_stepper_customerStep_title" />
      </Subtitle>
      <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
      {customerList ? (
        <>
          <Subtitle>
            <Trans i18nKey="cardEntry_customerInfo_customerList_text" />
          </Subtitle>
          <LongerTableContainer>
            <Table
              emptyTableText={t('cardEntry_customerInfo_notFound_text')}
              headers={customerTableHeaders.map((head) => t(head))}
              items={customerList}
              loading={customersLoading}
              onSelect={handleSelectCustomer}
              pageNumber={1}
              selectedItem={selectedCustomer}
              widths={['18', '18', '23', '18', '23']}
              withRadio
            />
          </LongerTableContainer>
          {selectedCustomer ? (
            <>
              <Subtitle>
                <Trans
                  i18nKey={
                    isProductTransfer
                      ? 'cardEntry_productInfo_productTransfer_cardList_text'
                      : 'cardEntry_cardList_text'
                  }
                />
              </Subtitle>
              <LongerTableContainer>
                <Table
                  emptyTableText={t('cardEntry_productInfo_supplementary_noCardFound_label')}
                  headers={(isProductTransfer
                    ? transferableCardTableHeaders
                    : cardTableHeaders
                  ).map((head) => t(head))}
                  items={isProductTransfer ? transferableCardTableData : cardTableData}
                  loading={cardsLoading}
                  onSelect={handleSelectCard}
                  pageNumber={1}
                  selectedItem={selectedCard}
                  widths={isProductTransfer ? ['33', '33', '34'] : ['25', '25', '25', '25']}
                  withRadio
                />
              </LongerTableContainer>
              {warning ? <WarningText>{warning}</WarningText> : null}
              <LongerButtonRow>
                <Button
                  color={ButtonColor.Primary}
                  onClick={handleContinue}
                  size={ButtonSize.Medium}
                  text={t('general_continue_button')}
                  type="button"
                />
              </LongerButtonRow>
            </>
          ) : null}
        </>
      ) : null}
    </FormModal>
  );
};

export default MainCardSelect;
